import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Divider,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  ShoppingCart,
  Add,
  Delete,
} from '@mui/icons-material';
import { api } from '../../services/apiClient';
import { formatCurrency } from '../../utils/currency';

interface QuickPurchaseOrderProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (po: any) => void;
}

interface Vendor {
  id: number;
  vendor_code: string;
  company_name: string;
  contact_person: string;
  email?: string;
  phone?: string;
  state?: string;
  gstin?: string;
  pan?: string;
  tds_category?: string;
}

interface POLineItem {
  description: string;
  hsnCode: string;
  quantity: number;
  unitPrice: number;
  gstRate: number;
  discountPercent: number;
}

const GST_RATES = [0, 5, 12, 18, 28];

const emptyItem: POLineItem = {
  description: '',
  hsnCode: '',
  quantity: 1,
  unitPrice: 0,
  gstRate: 18,
  discountPercent: 0,
};

const QuickPurchaseOrder: React.FC<QuickPurchaseOrderProps> = ({ open, onClose, onSuccess }) => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [vendorSearch, setVendorSearch] = useState('');
  const [items, setItems] = useState<POLineItem[]>([{ ...emptyItem }]);
  const [poDate, setPoDate] = useState(new Date().toISOString().split('T')[0]);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Company state (for determining GST type)
  const [companyState] = useState('Maharashtra');

  // Fetch vendors on search
  const fetchVendors = useCallback(async (search: string) => {
    if (search.length < 2) return;
    setLoadingVendors(true);
    try {
      const data = await api.get<Vendor[]>(`/procurement/vendors?search=${encodeURIComponent(search)}`);
      setVendors(data);
    } catch (err) {
      console.error('Error fetching vendors:', err);
    } finally {
      setLoadingVendors(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (vendorSearch) {
        fetchVendors(vendorSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [vendorSearch, fetchVendors]);

  // Set default delivery date (7 days from PO date)
  useEffect(() => {
    if (poDate) {
      const date = new Date(poDate);
      date.setDate(date.getDate() + 7);
      setDeliveryDate(date.toISOString().split('T')[0]);
    }
  }, [poDate]);

  // Calculate line total
  const calculateLineTotal = (item: POLineItem) => {
    const baseAmount = item.quantity * item.unitPrice;
    const discountAmount = baseAmount * (item.discountPercent / 100);
    return baseAmount - discountAmount;
  };

  // Calculate GST for a line item
  const calculateLineGST = (item: POLineItem, isInterState: boolean) => {
    const taxableAmount = calculateLineTotal(item);
    const gstAmount = taxableAmount * (item.gstRate / 100);

    if (isInterState) {
      return { cgst: 0, sgst: 0, igst: gstAmount };
    } else {
      return { cgst: gstAmount / 2, sgst: gstAmount / 2, igst: 0 };
    }
  };

  // Determine if inter-state transaction
  const isInterState = selectedVendor?.state && selectedVendor.state !== companyState;

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    let totalCgst = 0;
    let totalSgst = 0;
    let totalIgst = 0;

    items.forEach(item => {
      const lineTotal = calculateLineTotal(item);
      subtotal += lineTotal;

      const gst = calculateLineGST(item, !!isInterState);
      totalCgst += gst.cgst;
      totalSgst += gst.sgst;
      totalIgst += gst.igst;
    });

    const totalTax = totalCgst + totalSgst + totalIgst;
    const grandTotal = subtotal + totalTax;

    return { subtotal, totalCgst, totalSgst, totalIgst, totalTax, grandTotal };
  };

  const totals = calculateTotals();

  // Add new item
  const addItem = () => {
    setItems([...items, { ...emptyItem }]);
  };

  // Remove item
  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  // Update item
  const updateItem = (index: number, field: keyof POLineItem, value: string | number) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  // Submit PO
  const handleSubmit = async () => {
    // Validation
    if (!selectedVendor) {
      setError('Please select a vendor');
      return;
    }
    if (items.length === 0 || !items[0].description) {
      setError('Please add at least one item');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const poData = {
        vendor_id: selectedVendor.id,
        po_date: poDate,
        delivery_date: deliveryDate,
        items: items.map(item => ({
          description: item.description,
          hsn_code: item.hsnCode,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          gst_rate: item.gstRate,
          discount_percent: item.discountPercent,
        })),
        notes,
      };

      const po = await api.post('/procurement/purchase-orders', poData);
      onSuccess?.(po);
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create purchase order');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedVendor(null);
    setVendorSearch('');
    setVendors([]);
    setItems([{ ...emptyItem }]);
    setPoDate(new Date().toISOString().split('T')[0]);
    setDeliveryDate('');
    setNotes('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingCart color="primary" />
          <Typography variant="h6">New Purchase Order</Typography>
          {isInterState && (
            <Chip label="Inter-State (IGST)" color="warning" size="small" sx={{ ml: 2 }} />
          )}
          {!isInterState && selectedVendor && (
            <Chip label="Intra-State (CGST+SGST)" color="success" size="small" sx={{ ml: 2 }} />
          )}
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Vendor Selection */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              options={vendors}
              getOptionLabel={(option) => `${option.company_name} (${option.vendor_code})`}
              value={selectedVendor}
              onChange={(_, value) => setSelectedVendor(value)}
              onInputChange={(_, value) => setVendorSearch(value)}
              loading={loadingVendors}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vendor *"
                  placeholder="Search by name or code"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingVendors && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2">{option.company_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.vendor_code} | {option.state || 'No state'} | GSTIN: {option.gstin || 'N/A'}
                    </Typography>
                  </Box>
                </li>
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              label="PO Date"
              type="date"
              value={poDate}
              onChange={(e) => setPoDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              label="Expected Delivery"
              type="date"
              value={deliveryDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Vendor Info Display */}
          {selectedVendor && (
            <Grid size={{ xs: 12 }}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">Vendor</Typography>
                    <Typography variant="body2">{selectedVendor.company_name}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">GSTIN</Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {selectedVendor.gstin || 'Not provided'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">PAN</Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {selectedVendor.pan || 'Not provided'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">TDS Category</Typography>
                    <Typography variant="body2">{selectedVendor.tds_category || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Line Items */}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" color="primary">
                Order Items
              </Typography>
              <Button startIcon={<Add />} size="small" onClick={addItem}>
                Add Item
              </Button>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell width="25%">Description</TableCell>
                    <TableCell width="10%">HSN Code</TableCell>
                    <TableCell width="8%" align="right">Qty</TableCell>
                    <TableCell width="12%" align="right">Unit Price</TableCell>
                    <TableCell width="10%" align="right">Discount %</TableCell>
                    <TableCell width="10%">GST Rate</TableCell>
                    <TableCell width="15%" align="right">Amount</TableCell>
                    <TableCell width="5%"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Item description"
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="HSN"
                          value={item.hsnCode}
                          onChange={(e) => updateItem(index, 'hsnCode', e.target.value)}
                          inputProps={{ maxLength: 8 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, step: 1 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => updateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={item.discountPercent}
                          onChange={(e) => updateItem(index, 'discountPercent', parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, max: 100, step: 0.5 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          select
                          value={item.gstRate}
                          onChange={(e) => updateItem(index, 'gstRate', parseInt(e.target.value))}
                        >
                          {GST_RATES.map(rate => (
                            <MenuItem key={rate} value={rate}>{rate}%</MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          {formatCurrency(calculateLineTotal(item))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => removeItem(index)}
                          disabled={items.length === 1}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Totals */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes or terms..."
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Subtotal:</Typography>
                <Typography variant="body2">{formatCurrency(totals.subtotal)}</Typography>
              </Box>
              {!isInterState ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">CGST:</Typography>
                    <Typography variant="body2">{formatCurrency(totals.totalCgst)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">SGST:</Typography>
                    <Typography variant="body2">{formatCurrency(totals.totalSgst)}</Typography>
                  </Box>
                </>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">IGST:</Typography>
                  <Typography variant="body2">{formatCurrency(totals.totalIgst)}</Typography>
                </Box>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" fontWeight={600}>Grand Total:</Typography>
                <Typography variant="subtitle1" fontWeight={600} color="primary">
                  {formatCurrency(totals.grandTotal)}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <ShoppingCart />}
        >
          {loading ? 'Creating...' : 'Create Purchase Order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickPurchaseOrder;
